import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, LogIn, ChevronRight } from 'lucide-react';
import { BRAND, NAV_LINKS } from '../mock';
import { Button } from './ui/button';

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  const isHashLink = (to) => to.includes('#');

  return (
    <header className="sticky top-0 z-50">
      {/* Utility bar */}
      <div className="hidden md:block bg-[#4E4E50] text-white text-[13px]">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-end gap-6 h-9">
          <a href={BRAND.phoneHref} className="flex items-center gap-1.5 hover:text-[#4FC3E0] transition-colors">
            <Phone size={13} /> {BRAND.phone}
          </a>
          <span className="text-white/30">|</span>
          <a href={`mailto:${BRAND.email}`} className="hover:text-[#4FC3E0] transition-colors">{BRAND.email}</a>
          <span className="text-white/30">|</span>
          <span className="text-white/70">Serving Toronto &amp; the GTA</span>
        </div>
      </div>

      {/* Main nav */}
      <div className={`bg-white transition-shadow ${scrolled ? 'shadow-md' : 'shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-[72px]">
          <Link to="/" className="flex items-center shrink-0" aria-label="StreetFleet home">
            <img src={BRAND.logo} alt="StreetFleet courier logo" className="h-11 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) =>
              isHashLink(l.to) ? (
                <a key={l.label} href={l.to} className="sf-underline text-[15px] font-semibold text-[#4E4E50] hover:text-[#17A2C4] transition-colors">
                  {l.label}
                </a>
              ) : (
                <Link key={l.label} to={l.to} className="sf-underline text-[15px] font-semibold text-[#4E4E50] hover:text-[#17A2C4] transition-colors">
                  {l.label}
                </Link>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link to="/quote">
              <Button variant="outline" className="border-[#17A2C4] text-[#17A2C4] hover:bg-[#17A2C4] hover:text-white font-semibold">
                Get a Quote
              </Button>
            </Link>
            <a href={BRAND.loginUrl} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#17A2C4] hover:bg-[#128aa8] text-white font-semibold gap-1.5">
                <LogIn size={16} /> Customer Login
              </Button>
            </a>
          </div>

          <button className="lg:hidden text-[#4E4E50] p-2" onClick={() => setOpen(!open)} aria-label="Toggle menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Accent bar */}
      <div className="h-1 bg-gradient-to-r from-[#17A2C4] to-[#4FC3E0]" />

      {/* Mobile menu */}
      <div className={`lg:hidden bg-white border-b overflow-hidden transition-all duration-300 ${open ? 'max-h-[520px]' : 'max-h-0'}`}>
        <nav className="flex flex-col px-5 py-2">
          {NAV_LINKS.map((l) =>
            isHashLink(l.to) ? (
              <a key={l.label} href={l.to} className="flex items-center justify-between py-3.5 border-b border-gray-100 text-[#4E4E50] font-semibold">
                {l.label} <ChevronRight size={18} className="text-gray-400" />
              </a>
            ) : (
              <Link key={l.label} to={l.to} className="flex items-center justify-between py-3.5 border-b border-gray-100 text-[#4E4E50] font-semibold">
                {l.label} <ChevronRight size={18} className="text-gray-400" />
              </Link>
            )
          )}
          <div className="flex flex-col gap-3 py-4">
            <Link to="/quote">
              <Button variant="outline" className="w-full border-[#17A2C4] text-[#17A2C4] hover:bg-[#17A2C4] hover:text-white font-semibold">
                Get a Quote
              </Button>
            </Link>
            <a href={BRAND.loginUrl} target="_blank" rel="noopener noreferrer">
              <Button className="w-full bg-[#17A2C4] hover:bg-[#128aa8] text-white font-semibold gap-1.5">
                <LogIn size={16} /> Customer Login
              </Button>
            </a>
            <a href={BRAND.phoneHref}>
              <Button variant="outline" className="w-full border-[#4E4E50] text-[#4E4E50] font-semibold gap-1.5">
                <Phone size={16} /> {BRAND.phone}
              </Button>
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
}
